import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { AuthProvider } from "./contexts/AuthContext";
import { SidebarProvider } from "./contexts/SidebarContext";
import { PublicRoute, ProtectedRoute } from "./components/auth/AuthGuard";
import { configureErrorReporting } from "./utils/errorReporting";
// (removed debug-only scroll logging)

// Route-based code splitting for heavy pages/components
const AppLayout = lazy(() => import("./components/layout/AppLayout"));
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const FabricRequest = lazy(() => import("./pages/FabricRequest"));
const PublicFabricRequest = lazy(() => import("./pages/PublicFabricRequest"));
const PublicBuyerRequests = lazy(() => import('./pages/PublicBuyerRequests'));
const AuthenticatedFabricRequest = lazy(() => import("./pages/AuthenticatedFabricRequest"));
const FabricRequestView = lazy(() => import("./pages/FabricRequestView"));
const BidDetails = lazy(() => import("./pages/BidDetails"));
const OrderTracking = lazy(() => import("./pages/OrderTracking"));
const BidManagement = lazy(() => import("./pages/BidManagement"));
const Profile = lazy(() => import("./pages/Profile"));
const ProfileCompletion = lazy(() => import("./pages/ProfileCompletion"));
const NotFound = lazy(() => import("./pages/NotFound"));
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'));

// Dev-only tools, lazy-loaded and gated to avoid loading in production
const DevErrorDashboard = import.meta.env.DEV
  ? lazy(() => import("./components/debug/ErrorDashboard").then(m => ({ default: m.DevErrorDashboard })))
  : null as unknown as React.ComponentType;
const TokenStatus = import.meta.env.DEV
  ? lazy(() => import("./components/debug/TokenStatus").then(m => ({ default: m.TokenStatus })))
  : null as unknown as React.ComponentType;

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Configure error reporting system
    configureErrorReporting({
      enabled: true,
      environment: import.meta.env.MODE as 'development' | 'staging' | 'production',
      logToConsole: import.meta.env.DEV,
      sendToAnalytics: !import.meta.env.DEV,
      sendToMonitoring: import.meta.env.PROD
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <AuthProvider>
            <SidebarProvider>
              <Toaster />
          <Sonner />

            <Suspense fallback={<div className="p-8 text-center">Loading…</div>}>
              <Routes>
              {/* Public routes - redirect authenticated users to dashboard */}
              <Route path="/" element={
                <PublicRoute>
                  <Index />
                </PublicRoute>
              } />
              
              {/* Auth routes - for signup/login */}
              <Route path="/auth" element={
                <PublicRoute>
                    <Auth />
                </PublicRoute>
              } />
                
                {/* PUBLIC buyer dashboard preview (redirect to authenticated dashboard) */}
                <Route path="/public/my-requests" element={<Navigate to="/fabric-requests" replace />} />

                {/* 
                  NOTE: Public request creation flow is intentionally not enabled for launch.
                  The intended future experience is to let unauthenticated users begin a request
                  and require login before completion, but that flow is not fully wired for this release.
                  Current launch path is the authenticated request creation flow.
                */}
                {/* Public fabric request route - lead generation flow */}
                <Route path="/public/fabric-requests/create" element={<PublicFabricRequest />} />
              
              {/* Semi-public route - can be accessed without auth but requires auth for advanced features */}
                <Route path="/create-fabric-request" element={<FabricRequest />} />
              
              {/* Protected routes - require fabric buyer authentication */}
              <Route path="/fabric-request/view/:requestId" element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }>
                <Route index element={<FabricRequestView />} />
              </Route>
              
              <Route path="/fabric-request/view/:requestRef/bid/:bidRef" element={
                <ProtectedRoute>
                  <BidDetails />
                </ProtectedRoute>
              } />
              
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }>
                  <Route index element={<Dashboard />} />
              </Route>
              
               <Route path="/fabric-requests" element={
               <ProtectedRoute>
                 <AppLayout />
               </ProtectedRoute>
             }>
               <Route index element={<PublicBuyerRequests />} />
             </Route>
                
                {/* RESTful fabric request routes */}
                <Route path="/fabric-requests/create" element={
                  <ProtectedRoute>
                    <AuthenticatedFabricRequest />
                  </ProtectedRoute>
                } />
              
              <Route path="/messages" element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }>
                <Route index element={<div className="p-8">Messages coming soon...</div>} />
              </Route>
              
              <Route path="/wallet" element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }>
                <Route index element={<div className="p-8">Wallet coming soon...</div>} />
              </Route>
              
              {/* Profile and settings - protected */}
              <Route path="/profile" element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }>
                  <Route index element={<Profile />} />
              </Route>
              
              {/* Profile completion - protected */}
              <Route path="/profile-completion" element={
                <ProtectedRoute>
                  <ProfileCompletion />
                </ProtectedRoute>
              } />
              
              {/* Order tracking route */}
              <Route path="/order-tracking/:orderId" element={
                <ProtectedRoute>
                  <OrderTracking />
                </ProtectedRoute>
              } />
              {/* NOTE: `/bids` is intentionally a placeholder route for now.
                  The BidManagement screen exists but is not currently enabled in this flow.
                  Do not switch this route back to BidManagement unless product explicitly requires it. */}
              <Route path="/bids" element={<div className="p-8 text-center">Bids page coming soon...</div>} />
              
              {/* Google OAuth callback */}
              <Route path="/auth/google/callback" element={
                <PublicRoute>
                  <GoogleCallback />
                </PublicRoute>
              } />
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          

          {/* Development tools - only show in development, lazy-loaded */}
          {import.meta.env.DEV && DevErrorDashboard && (
            <Suspense fallback={null}>
              <DevErrorDashboard />
            </Suspense>
          )}
          {import.meta.env.DEV && TokenStatus && (
            <Suspense fallback={null}>
              <TokenStatus />
            </Suspense>
          )}
          </SidebarProvider>
        </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
